import React, { useMemo, useState } from 'react';
import { FileTextIcon, SendIcon, BellIcon, CheckCircleIcon, AlertTriangleIcon, ScaleIcon, ChevronRightIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { CONTRACTS, CONTRACT_TEMPLATES, BOOKING_FILES } from '../../data/windsor';
import { contractStatusMeta } from './bookingHelpers';
import type { BookingFile, ContractStatus } from '../../types/loisirs';
interface Props {
  query: string;
  onOpenBooking: (booking: BookingFile) => void;
}
type Filter = 'tous' | ContractStatus;
export function ContractsQueue({
  query,
  onOpenBooking
}: Props) {
  const {
    t,
    lang
  } = useLanguage();
  const [filter, setFilter] = useState<Filter>('tous');
  const [reminded, setReminded] = useState<string[]>([]);
  const counts = useMemo(() => {
    const base: Record<ContractStatus, number> = {
      a_generer: 0,
      envoye: 0,
      signe: 0,
      en_retard: 0
    };
    CONTRACTS.forEach(c => {
      base[c.status] += 1;
    });
    return base;
  }, []);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CONTRACTS.filter(c => filter === 'tous' ? true : c.status === filter).filter(c => q ? c.locataire.toLowerCase().includes(q) || c.title.toLowerCase().includes(q) || c.number.toLowerCase().includes(q) : true);
  }, [filter, query]);
  const filters: {
    id: Filter;
    label: string;
    count: number;
  }[] = [{
    id: 'tous',
    label: t('Tous', 'All'),
    count: CONTRACTS.length
  }, {
    id: 'a_generer',
    label: t('À générer', 'To generate'),
    count: counts.a_generer
  }, {
    id: 'envoye',
    label: t('En attente de signature', 'Awaiting signature'),
    count: counts.envoye
  }, {
    id: 'signe',
    label: t('Signés', 'Signed'),
    count: counts.signe
  }, {
    id: 'en_retard',
    label: t('En retard', 'Overdue'),
    count: counts.en_retard
  }];
  const bookingOf = (contractId: string) => BOOKING_FILES.find(b => b.contractId === contractId);
  return <div className="space-y-4" data-id="element-3115" data-mp="2oj2vpvvigugj">
      {/* Status filters */}
      <div className="flex flex-wrap items-center gap-2" data-id="element-3116" data-mp="2oknphja049dc">
        {filters.map(item => {
        const active = filter === item.id;
        return <button key={item.id} onClick={() => setFilter(item.id)} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium border transition-colors ${active ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`} data-id="element-3117" data-mp="2ygr2jk6e5sf2">
              {item.label}
              <span className={`text-xs font-bold tabular-nums ${active ? 'text-slate-300' : item.id === 'en_retard' && item.count > 0 ? 'text-red-600' : 'text-slate-400'}`} data-id="element-3118" data-mp="3h9l6lj2h804i">
                {item.count}
              </span>
            </button>;
      })}
      </div>

      {visible.length === 0 ? <div className="bg-white rounded-xl border border-slate-200 py-16 text-center" data-id="element-3119" data-mp="4n4fjitul1ja">
          <div className="mx-auto w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-3" data-id="element-3120" data-mp="9lxulfos90lk">
            <FileTextIcon className="w-6 h-6 text-slate-400" data-id="element-3121" data-mp="3r2p5dl3r3580" />
          </div>
          <p className="text-sm font-semibold text-slate-700" data-id="element-3122" data-mp="3vugyo3eajp1u">
            {t('Aucun contrat dans cette file', 'No contract in this queue')}
          </p>
          <p className="text-xs text-slate-500 mt-1" data-id="element-3123" data-mp="3i0ppwx8dw976">
            {t('Rien à traiter pour le moment.', 'Nothing to process right now.')}
          </p>
        </div> : <ul className="space-y-2" data-id="element-3124" data-mp="3d3pr97dv68q3">
          {visible.map(contract => {
        const meta = contractStatusMeta(contract.status);
        const template = CONTRACT_TEMPLATES.find(tp => tp.id === contract.templateId);
        const booking = bookingOf(contract.id);
        const wasReminded = reminded.includes(contract.id);
        return <li key={contract.id} className="group bg-white rounded-xl border border-slate-200 px-5 py-4 hover:shadow-sm transition-shadow" data-id="element-3125" data-mp="5kcodswmrqlx">
                <div className="flex items-start gap-4" data-id="element-3126" data-mp="ivpg2xpie7z">
                  <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${contract.status === 'signe' ? 'bg-emerald-50 text-emerald-600' : contract.status === 'en_retard' ? 'bg-red-50 text-red-600' : contract.status === 'envoye' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`} data-id="element-3127" data-mp="3rqrqw5bpps53">
                    {contract.status === 'signe' ? <CheckCircleIcon className="w-4 h-4" data-id="element-3128" data-mp="1tg9darmeon7v" /> : contract.status === 'en_retard' ? <AlertTriangleIcon className="w-4 h-4" data-id="element-3129" data-mp="1yb6jlqsawih2" /> : <FileTextIcon className="w-4 h-4" data-id="element-3130" data-mp="239zx3j7l89h8" />}
                  </span>

                  <div className="min-w-0 flex-1" data-id="element-3131" data-mp="15m9aad62hw0g">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-3132" data-mp="1akavb3n205ek">
                      <p className="text-sm font-semibold text-[#0F172A]" data-id="element-3133" data-mp="1fikppc0yf1z4">
                        {contract.locataire}
                      </p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${meta.className}`} data-id="element-3134" data-mp="1jlhghq0ne4e5">
                        {meta[lang]}
                      </span>
                      {contract.free && <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium border border-slate-200" data-id="element-3135" data-mp="vtjuyjkt2brx">
                          {t('0 $', '$0')}
                        </span>}
                      {contract.source === 'greffe' && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs font-medium border border-slate-200" data-id="element-3136" data-mp="zvol38c1oq9t">
                          <ScaleIcon className="w-3 h-3" data-id="element-3137" data-mp="84qv4c12bjs1" />
                          {t('Greffe', 'Clerk')}
                        </span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 tabular-nums" data-id="element-3138" data-mp="lxzztze2fh48">
                      {contract.number} · {contract.title}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5" data-id="element-3139" data-mp="3pjxlvlrcdnuv">
                      {template?.name[lang]}
                      {template?.eSignature ? ` · ${t('signature électronique', 'e-signature')}` : ` · ${t('signature manuscrite', 'handwritten signature')}`}
                      {contract.signedBy && ` · ${contract.signedBy}`}
                    </p>
                    {wasReminded && <p className="flex items-center gap-1.5 text-xs text-emerald-700 mt-1.5" data-id="element-3140" data-mp="1w2j9hwcyu20y">
                        <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-3141" data-mp="2js42z96qwhoh" />
                        {t('Relance envoyée', 'Reminder sent')}
                      </p>}
                  </div>

                  <div className="flex items-center gap-2 shrink-0" data-id="element-3142" data-mp="29twp4z7r6fqp">
                    {contract.status === 'a_generer' && <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-xs font-semibold rounded-lg transition-colors" data-id="element-3143" data-mp="2es7w926nyc58">
                        <FileTextIcon className="w-3.5 h-3.5" data-id="element-3144" data-mp="13edmt42cq2yh" />
                        {t('Générer', 'Generate')}
                      </button>}
                    {(contract.status === 'envoye' || contract.status === 'en_retard') && <button onClick={() => setReminded(prev => prev.includes(contract.id) ? prev : [...prev, contract.id])} disabled={wasReminded} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-xs font-semibold rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-40" data-id="element-3145" data-mp="1l39c3hbe4xo4">
                        {contract.status === 'en_retard' ? <BellIcon className="w-3.5 h-3.5" data-id="element-3146" data-mp="2wjplou09kk32" /> : <SendIcon className="w-3.5 h-3.5" data-id="element-3147" data-mp="2rqd6tbldzrni" />}
                        {t('Relancer', 'Send reminder')}
                      </button>}
                    {booking && <button onClick={() => onOpenBooking(booking)} aria-label={t('Ouvrir le dossier', 'Open the file')} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-slate-500 rounded-lg opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-slate-100 transition-all" data-id="element-3148" data-mp="3910a8x32rxyb">
                        #{booking.number}
                        <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-3149" data-mp="3rst2gpablf4z" />
                      </button>}
                  </div>
                </div>
              </li>;
      })}
        </ul>}
    </div>;
}